import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import api from '../utils/api';

interface DeletionStatus {
  isDeletionPending: boolean;
  deletionScheduledAt: string | null;
}

export interface UseAccountDeletionReturn {
  isDeletionPending: boolean;
  deletionScheduledAt: string | null;
  loading: boolean;
  error: string | null;
  requestDeletion: (password: string) => Promise<void>;
  cancelDeletion: () => Promise<void>;
}

export const useAccountDeletion = (): UseAccountDeletionReturn => {
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState<DeletionStatus>({
    isDeletionPending: false,
    deletionScheduledAt: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/user/deletion-status');
      setStatus({
        isDeletionPending: response.data.isDeletionPending,
        deletionScheduledAt: response.data.deletionScheduledAt,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch deletion status');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const requestDeletion = async (password: string): Promise<void> => {
    const response = await api.post('/api/user/delete-request', { password });
    setStatus({
      isDeletionPending: true,
      deletionScheduledAt: response.data.deletionScheduledAt,
    });
  };

  const cancelDeletion = async (): Promise<void> => {
    await api.post('/api/user/cancel-deletion');
    setStatus({
      isDeletionPending: false,
      deletionScheduledAt: null,
    });
  };

  return {
    isDeletionPending: status.isDeletionPending,
    deletionScheduledAt: status.deletionScheduledAt,
    loading,
    error,
    requestDeletion,
    cancelDeletion,
  };
};
